import { useLoadingSpinner } from "hooks/useLoadingSpinner";
import Button from "components/Button";
import Modal, { MODAL_ACTIONS, MODAL_BODY } from "components/Modal";
import Lottie from "lottie-react";
import React, { useState } from "react";
import { cn } from "utils/cn";
import { httpStatusOf } from "utils/httpStatusOf";

interface DeleteModalProps {
  show: boolean;
  onClose: () => void;
  onDelete: () => void | Promise<void>;
  title?: string;
  message?: string;
  itemName?: string;
  deleteButtonText?: string;
  cancelButtonText?: string;
  isLoading?: boolean;
}

/**
 * The delete confirm, in the app's modal (2026-10-23): the same shell as Photoshoot Studio's "Enter Prompt" — the
 * `Modal`'s own title bar with its ✕, a quiet line of text, the actions at the foot on the right. Its props and test ids
 * are unchanged, so every caller keeps working.
 */
const DeleteModal: React.FC<DeleteModalProps> = ({
  onClose,
  show,
  onDelete,
  title = "Delete",
  message = "Are you sure you want to delete this item? This action cannot be undone.",
  itemName,
  deleteButtonText = "Delete",
  cancelButtonText = "Cancel",
  isLoading = false,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const loadingSpinner = useLoadingSpinner();

  async function handleDelete() {
    try {
      setIsDeleting(true);
      await onDelete();
      onClose();
    } catch (error) {
      console.error("Delete failed", { status: httpStatusOf(error) });
    } finally {
      setIsDeleting(false);
    }
  }

  const finalMessage = itemName ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.` : message;
  const busy = isDeleting || isLoading;

  return (
    <Modal onClose={onClose} show={show} showCloseButton title={title} bodyClassName="!w-full !max-w-[440px]">
      <div data-testid="delete-modal" className="flex flex-col">
        <span className={cn(MODAL_BODY, "text-bodySm text-grey")}>{finalMessage}</span>
        <div className={MODAL_ACTIONS}>
          <Button data-testid="delete-modal-cancel-btn" className="btn-secondary" onClick={onClose} disabled={busy}>
            {cancelButtonText}
          </Button>
          <Button data-testid="delete-modal-confirm-btn" className="btn-warning" onClick={handleDelete} disabled={busy}>
            {isDeleting ? <Lottie animationData={loadingSpinner} loop={true} className="h-8 w-8" /> : deleteButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteModal;
